import {
	Button,
	Spinner,
	Text,
	mergeClasses,
	TabList,
	Tab,
	TabValue,
	SelectTabEvent,
	SelectTabData,
} from "@fluentui/react-components";
import { useStyle } from "./style";
import { useShadowStyle } from "@/common/theme/shadowStyles";
import { ContactGroupType, ContactsPanelProps } from "./types";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { shadowByPosition } from "@/common/theme/styleUtils";
import { Flex, GapSize } from "@/components/atoms/flex";
import { useIntl } from "react-intl";
import {
	CommentMultiple24Regular,
	Mail20Regular,
	PeopleAdd24Regular,
	PeopleFilled,
	PeopleQueueFilled,
	Search24Regular,
	StarFilled,
	PersonNote20Regular,
} from "@fluentui/react-icons";
import { PanelHeader } from "@/components/atoms/panelHeader";
import {
	ActiveFilterChips,
	AdvancedFilterButton,
	AdvancedFilterDialog,
	AdvancedFilterFieldKey,
	countAdvancedFilterValues,
	emptyAdvancedFilter,
	isAdvancedFilterEmpty,
	normalizeAdvancedFilter,
	removeAdvancedFilterValue,
	toAdvancedFilterRequest,
} from "./advancedFilter";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import { useContactsAdvancedFilterStore } from "@/common/stores/nonpersistingStores/contactsAdvancedFilter/contactsAdvancedFilterStore";
import {
	ContactsAdvancedFilterActions,
	ContactsAdvancedFilterState,
} from "@/common/stores/nonpersistingStores/contactsAdvancedFilter/types";
import {
	ContactsKey,
	MyQueuesContactsKey,
	FavoritesKey,
	addToFavorites,
	fetchContacts,
	fetchMyQueuesContacts,
	fetchFavorites,
	removeContact,
	removeFromFavorites,
	addContact,
	updateContact,
} from "@/repository/queryFns";
import { List, ListItemProps } from "@/components/atoms/list";
import { InViewHookResponse } from "react-intersection-observer";
import {
	ContactDto,
	ContactListItem,
	ContactsAdvancedFilter,
	ContactType,
} from "@/repository/models";
import { useInfiniteQueryHook } from "../../../hooks/infiniteQuery";
import { CallMenuItems } from "./CallMenuItems";
import { ExtendedMenuItems } from "./ExtendedMenuItems";
import { Query, useMutation, useQueryClient } from "@tanstack/react-query";
import { ExtendedPagedResult, PagedResult } from "@/repository/pagedResult";
import { ClearableInput } from "@/components/atoms/clearableInput";
import {
	AppCacheActions,
	AppCacheState,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { PopoverView } from "@/components/atoms/popover";
import { ProfilePicture } from "@/components/organisms/profilePicture";
import { ContactAvatar } from "./ContactAvatar";
import { useTransferHook } from "@/hooks/transfer";
import {
	CustomerHistoryMediaType,
	CallTargetType,
	InteractionType,
	TransferType,
} from "@/common/types";
import { DialogView } from "@/components/atoms/dialog";
import { useMessage } from "@/common/messages";
import { ContactForm } from "./contactForm";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { ConfirmationDialog } from "@/components/atoms/dialog/ConfirmationDialog";
import {
	CurrentInteractionActions,
	CurrentInteractionState,
	NewInteraction,
	NewInteractionData,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	NavigationActions,
	useNavigationStore,
	ToggleTabType,
} from "@/common/stores/persistingStores/navigation";
import { useVoiceOutbound } from "@/hooks/outbound";
import { getTeamsPresences } from "@/repository/queryFns/teams";
import { TeamsPresence } from "@/repository/models/teams";
import { mapPresenceToStatus } from "@/common/mappers/presenceMapper";
import { useDebounce } from "@/common/helpers/useDebounce";
import { openUserChat } from "@/common/helpers/teamsHelper";
import {
	AgendaContentPopover,
	useAgendaStyle,
} from "@/components/molecules/agendaContentPopover";
import { useEmailOutbound } from "@/hooks/outbound";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { OperatorTemplates } from "./OperatorTemplates";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { callSessionStatesAllowingOperatorEmail } from "@/common/helpers";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import { TargetSelectionSource } from "@/repository/models/outboundCall";
import { InteractionNotesPopover } from "@/components/organisms/interactionNotes";
import { Note, NoteType } from "@/components/organisms/history/types";
import { getNotesByContactId } from "@/repository/queryFns/contacts";
import {
	AgentPermissionsState,
	AgentState,
	useAgentStore,
} from "@/common/stores/persistingStores/agentPermissions";
import { QueueFilter } from "@/components/molecules/queueFilter/QueueFilter";
import { ContactTypeFilter } from "@/components/molecules/contactTypeFilter/ContactTypeFilter";
import { ContactTypeFilterValue } from "@/components/molecules/contactTypeFilter/types";
import { DialogSize } from "@/components/atoms/dialog/types";
import { QueryResult } from "@/hooks/infiniteQuery";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const ContactsPanel = (props: ContactsPanelProps) => {
	const { shadowTo } = props;
	const popoverContainerRef: React.MutableRefObject<HTMLDivElement | null> =
		useRef(null);
	const notesPopoverContainerRef: React.MutableRefObject<HTMLDivElement | null> =
		useRef(null);
	const intl = useIntl();

	const ContactsQueueIdsCacheKey: CacheKey = CacheKey.ContactsQueueIds;

	const agendaClasses = useAgendaStyle();
	const shadowStyle = useShadowStyle();
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const {
		isOutboundPossible,
		isOutboundAllowed,
		hasAnyOutboundRightEndpoints,
	} = useVoiceOutbound();
	const showNewInteraction = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions.showNew
	);

	const classes = useStyle();

	const { isMobileMedium } = useResponsiveBreakpoints();

	const email = useEmailOutbound();

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);
	const [searchText, setSearchText] = useState<string>(
		appStoreCache[CacheKey.ContactsSearch] ?? ""
	);
	const [filteredQueueIds, setFilteredQueueIds] = useState<string[]>(
		appStoreCache[CacheKey.ContactsQueueIds] ?? []
	);
	const [contactTypeFilter, setContactTypeFilter] =
		useState<ContactTypeFilterValue>(
			appStoreCache[CacheKey.ContactsTypeFilter] ?? ContactTypeFilterValue.All
		);

	const canUseAdvancedFilter = useAgentPermissionsStore(
		(state: AgentPermissionsState) =>
			state.isSupervisor || state.hasOperatorRights
	);

	const [advancedFilter, setAdvancedFilter] = useState<ContactsAdvancedFilter>(
		() => normalizeAdvancedFilter(appStoreCache[CacheKey.ContactsAdvancedFilter])
	);

	const isAdvancedFilterDialogOpen = useContactsAdvancedFilterStore(
		(state: ContactsAdvancedFilterState) => state.isDialogOpen
	);

	const setAdvancedFilterDialogOpen = useContactsAdvancedFilterStore(
		(state: ContactsAdvancedFilterActions) => state.setDialogOpen
	);

	useEffect(() => {
		return () => setAdvancedFilterDialogOpen(false);
	}, [setAdvancedFilterDialogOpen]);

	const onAdvancedFilterChange = useCallback(
		(value: ContactsAdvancedFilter) => {
			setAdvancedFilter(value);
			appStoreActions.setCache(CacheKey.ContactsAdvancedFilter, value);
		},
		[appStoreActions]
	);

	const onContactTypeFilterChange = useCallback(
		(value: ContactTypeFilterValue) => {
			setContactTypeFilter(value);
			appStoreActions.setCache(CacheKey.ContactsTypeFilter, value);
		},
		[appStoreActions]
	);

	const mapContactTypeFilter = useCallback(
		(value: ContactTypeFilterValue): ContactType | undefined => {
			switch (value) {
				case ContactTypeFilterValue.Internal:
					return ContactType.AzureActiveDirectory;
				case ContactTypeFilterValue.External:
					return ContactType.Database;
				default:
					return undefined;
			}
		},
		[]
	);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const { show: showMessage } = useMessage();

	const [isContactDialogOpen, setIsContactDialogOpen] =
		useState<boolean>(false);
	const [isAgendaDialogOpen, setIsAgendaDialogOpen] = useState<boolean>(false);
	const [agendaContactUPN, setAgendaContactUPN] = useState<string>("");

	const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
		useState<boolean>(false);

	const [notesContactId, setNotesContactId] = useState<string | undefined>(
		undefined
	);
	const [contactNotes, setContactNotes] = useState<Note[]>([]);

	const [contactInEditModeOrToBeDeleted, setContactInEditModeOrToBeDeleted] =
		useState<ContactListItem>({} as ContactListItem);

	const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);

	const queryClient = useQueryClient();

	const [startTransfer] = useTransferHook();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const agentState = useAgentStore((state: AgentState) => state);

	const contactNotesEnabled = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.contactNotesEnabled
	);

	const [
		myQueuesContactsResult,
		myQueuesContactsTotalCount,
		myQueuesContactsInView,
		setMyQueuesContactsSearch,
		,
		setMyQueuesFilter,
		updateMyQueueItem,
	] = useInfiniteQueryHook<ContactListItem>(
		MyQueuesContactsKey,
		fetchMyQueuesContacts,
		CacheKey.ContactsSearch,
		true,
		undefined,
		undefined,
		undefined,
		false
	);

	const [
		contactsResult,
		contactsTotalCount,
		contactsInView,
		setContactsSearch,
		,
		setContactsFilter,
		updateContactsItem,
	] = useInfiniteQueryHook<ContactListItem>(
		ContactsKey,
		fetchContacts,
		CacheKey.ContactsSearch,
		true,
		undefined,
		undefined,
		undefined,
		false
	);

	const [
		favoritesResult,
		favoritesTotalCount,
		favoritesInView,
		setFavoritesSearch,
		,
		setFavoritesFilter,
		updateFavoritesItem,
	] = useInfiniteQueryHook<ContactListItem>(
		FavoritesKey,
		fetchFavorites,
		CacheKey.ContactsSearch,
		appSettings.favoritesIsEnabled,
		undefined,
		undefined,
		undefined,
		false
	);

	useEffect(() => {
		if (isMobileMedium) {
			setIsContactDialogOpen(false);
		}
	}, [isMobileMedium]);

	const shadow = useMemo<string | undefined>(
		() => shadowByPosition(shadowTo, shadowStyle),
		[shadowTo, shadowStyle]
	);

	const [selectedValue, setSelectedValue] = useState<TabValue>(
		ContactGroupType.All
	);

	useEffect(() => {
		const timerId = setTimeout(() => {
			setFavoritesSearch(searchText);
			setContactsSearch(searchText);
			setMyQueuesContactsSearch(searchText);
		}, 500);

		return () => clearTimeout(timerId);
	}, [
		searchText,
		setFavoritesSearch,
		setContactsSearch,
		setMyQueuesContactsSearch,
	]);

	useEffect(() => {
		const contactType = mapContactTypeFilter(contactTypeFilter);
		const advancedFilterToApply =
			canUseAdvancedFilter && !isAdvancedFilterEmpty(advancedFilter)
				? toAdvancedFilterRequest(advancedFilter)
				: undefined;

		setContactsFilter({
			queueIds: filteredQueueIds,
			contactType,
			advancedFilter: advancedFilterToApply,
		});
		setMyQueuesFilter({
			queueIds: filteredQueueIds,
			advancedFilter: advancedFilterToApply,
		});
		setFavoritesFilter({
			queueIds: filteredQueueIds,
			contactType,
			advancedFilter: advancedFilterToApply,
		});
	}, [
		filteredQueueIds,
		contactTypeFilter,
		mapContactTypeFilter,
		advancedFilter,
		canUseAdvancedFilter,
	]);

	useEffect(() => {
		if (selectedValue === ContactGroupType.MyQueues) {
			onContactTypeFilterChange(ContactTypeFilterValue.All);
		}
	}, [selectedValue, onContactTypeFilterChange]);

	const mapContactDescription = useCallback(
		(contact: ContactListItem) =>
			[contact.jobTitle, contact.department]
				.filter((x: string) => !!x)
				.join(" / "),
		[]
	);

	const removeContactFromCache = useCallback(
		(contactId: string, key: string) => {
			queryClient.setQueriesData(
				{ predicate: (query: Query) => query.queryKey.includes(key) },
				(data: any) => {
					if (!data) {
						return data;
					}

					const pages = data.pages;
					const lastPage = pages[pages.length - 1];
					const newTotalCount =
						lastPage.totalCount >= 1 ? lastPage.totalCount - 1 : 0;
					const newRawTotalCount =
						(lastPage.newRawTotalCount || lastPage.totalCount) - 1;

					return {
						...data,
						pages: pages.map((p: ExtendedPagedResult<ContactListItem>) => ({
							items: p.items.filter((c: ContactListItem) => c.id !== contactId),
							totalCount: newTotalCount,
							rawTotalCount: newRawTotalCount,
						})),
					};
				}
			);
		},
		[queryClient]
	);

	const refetchQueries = useCallback(
		(key: string) => queryClient.refetchQueries({ queryKey: [key] }),
		[queryClient]
	);

	const invalidateQueries = useCallback(
		(key: string) => queryClient.invalidateQueries({ queryKey: [key] }),
		[queryClient]
	);

	notificationListener(ServerEvent.MyQueueContactsChanged, () => {
		refetchQueries(MyQueuesContactsKey);
	});

	const updateHasCC4TeamsClientIfNeeded = (
		contactResult: QueryResult<ContactListItem>,
		updateItem: (targetId: string, newItem: ContactListItem) => void,
		userTeamsClientChangedDto: any
	) => {
		const contact = contactResult.data?.pages
			.flatMap((x: PagedResult<ContactListItem>) => x.items)
			.find((x) => x.id === userTeamsClientChangedDto.userId);

		if (!contact) {
			return;
		}

		if (contact.hasCC4TeamsClient != userTeamsClientChangedDto.hasClient) {
			contact.hasCC4TeamsClient = userTeamsClientChangedDto.hasClient;

			updateItem(contact.id, contact);
		}
	};

	notificationListener(
		ServerEvent.UserTeamsClientChanged,
		(userTeamsClientChangedDto: any) => {
			updateHasCC4TeamsClientIfNeeded(
				contactsResult,
				updateContactsItem,
				userTeamsClientChangedDto
			);

			updateHasCC4TeamsClientIfNeeded(
				myQueuesContactsResult,
				updateMyQueueItem,
				userTeamsClientChangedDto
			);

			updateHasCC4TeamsClientIfNeeded(
				favoritesResult,
				updateFavoritesItem,
				userTeamsClientChangedDto
			);
		}
	);

	const removeContactMutation = useMutation({
		mutationFn: removeContact,
		onMutate: (contactId: string) => {
			removeContactFromCache(contactId, ContactsKey);
			removeContactFromCache(contactId, FavoritesKey);
			setIsConfirmationDialogOpen(false);
			setContactInEditModeOrToBeDeleted({} as ContactListItem);
			showMessage("global", {
				intent: "success",
				text: intl.formatMessage({
					id: "Contacts.SuccessRemove",
				}),
			});
		},
		onError: (err, data) => {
			captureError(err, "Error removing contact", undefined, {
				contactId: data,
			});
			showMessage("dialog", {
				intent: "error",
				text: intl.formatMessage({
					id: "Contacts.ErrorOnRemove",
				}),
			});
			refetchQueries(ContactsKey);
			refetchQueries(FavoritesKey);
		},
	});

	const addToFavoritesMutation = useMutation({
		mutationFn: addToFavorites,
		onSuccess: () => refetchQueries(FavoritesKey),
		onError: (err, data) => {
			captureError(err, "Error adding contact to favorites", undefined, {
				contactId: data,
			});

			refetchQueries(ContactsKey);
		},
	});

	const removeFromFavoriteMutation = useMutation({
		mutationFn: removeFromFavorites,
		onMutate: (contactId: string) =>
			removeContactFromCache(contactId, FavoritesKey),
		onSuccess: () => refetchQueries(ContactsKey),
		onError: (err, data) => {
			captureError(err, "Error removing contact from favorites", undefined, {
				contactId: data,
			});

			refetchQueries(FavoritesKey);
		},
	});

	const updateContactMutation = useMutation({
		mutationFn: updateContact,
		onSuccess: (_: ContactDto) => {
			setIsContactDialogOpen(false);
			setIsSaveInProgress(false);
			showMessage("global", {
				intent: "success",
				text: intl.formatMessage({
					id: "Contacts.SuccessUpdate",
				}),
			});

			setTimeout(() => {
				invalidateQueries(ContactsKey);
				invalidateQueries(FavoritesKey);
			}, 1000);
		},
		onError: (err, data) => {
			captureError(err, "Error updating contact", undefined, data);
			setIsSaveInProgress(false);
			showMessage("dialog", {
				intent: "error",
				text: intl.formatMessage({
					id: "Contacts.ErrorOnSave",
				}),
			});
		},
	});

	const addContactMutation = useMutation({
		mutationFn: addContact,
		onSuccess: (_: ContactDto) => {
			setIsContactDialogOpen(false);
			setIsSaveInProgress(false);
			showMessage("global", {
				intent: "success",
				text: intl.formatMessage({
					id: "Contacts.SuccessAdd",
				}),
			});
			setTimeout(() => {
				invalidateQueries(ContactsKey);
			}, 1000);
		},
		onError: (err, data) => {
			captureError(err, "Error creating contact", undefined, data);
			setIsSaveInProgress(false);
			showMessage("dialog", {
				intent: "error",
				text: intl.formatMessage({
					id: "Contacts.ErrorOnSave",
				}),
			});
		},
	});

	const saveContact = (contact: ContactDto) => {
		addBreadcrumb(
			"ui_interaction",
			`Updating or creating contact with id ${contactInEditModeOrToBeDeleted.id}`,
			contact
		);
		const interactionId = currentInteraction?.id ?? "";
		const interactionType = getCustomerHistoryMediaType(
			currentInteraction?.type
		);

		setIsSaveInProgress(true);

		if (contact.id) {
			updateContactMutation.mutate({
				contact,
				interactionId,
				interactionType,
			});
		} else {
			addContactMutation.mutate({
				contact,
				interactionId,
				interactionType,
			});
		}
	};

	const getCustomerHistoryMediaType = (
		type: InteractionType | null | undefined
	) => {
		switch (type) {
			case InteractionType.Call:
				return CustomerHistoryMediaType.Call;
			case InteractionType.Email:
				return CustomerHistoryMediaType.Email;
			case InteractionType.Voicemail:
				return CustomerHistoryMediaType.Voicemail;
			case InteractionType.WhatsApp:
				return CustomerHistoryMediaType.WhatsApp;
			case InteractionType.Webchat:
				return CustomerHistoryMediaType.Webchat;
			default:
				return CustomerHistoryMediaType.Call;
		}
	};

	const closeContactDialog = () => {
		setContactInEditModeOrToBeDeleted({} as ContactListItem);
		setIsContactDialogOpen(false);
	};

	const handleTransferClick = useCallback(
		(type: TransferType, transferTarget: string) => {
			startTransfer(type, transferTarget);
		},
		[startTransfer]
	);

	const handleOnCallClick = (
		type: CallTargetType,
		contact: ContactListItem
	) => {
		const targetValue = {
			[CallTargetType.Sip]: contact.userPrincipalName,
			[CallTargetType.Bussiness]: contact.businessPhone,
			[CallTargetType.Mobile]: contact.mobilePhone,
		};

		addBreadcrumb("ui_interaction", "Initiate outbound call", {
			type,
			contact,
		});

		const newInteractionData: NewInteractionData = {
			name: contact.name || "",
			value: targetValue[type] || "",
			selectionSource: TargetSelectionSource.ContactList,
			selectionType: type,
			contactId: contact.id,
		};

		var newInteraction: NewInteraction = {
			type: InteractionType.Call,
			data: newInteractionData,
		};

		appStoreActions.setCache(CacheKey.OutboundType, InteractionType.Call);

		setToggledTab(ToggleTabType.NewInteraction);
		showNewInteraction(newInteraction);
	};

	const handleOnEmailClick = (contact: ContactListItem) => {
		var newInteractionData: NewInteractionData = {
			name: contact.name || "",
			value: contact.userPrincipalName || contact.email || "",
			selectionSource: TargetSelectionSource.ContactList,
			contactId: contact.id,
		};

		var newInteraction: NewInteraction = {
			type: InteractionType.Email,
			data: newInteractionData,
		};

		appStoreActions.setCache(CacheKey.OutboundType, InteractionType.Email);

		setToggledTab(ToggleTabType.NewInteraction);
		showNewInteraction(newInteraction);
	};

	const areOperatorTemplatesVisible = useMemo<boolean>(() => {
		if (
			!callSession ||
			(callSession?.state != undefined &&
				!callSessionStatesAllowingOperatorEmail.includes(callSession?.state)) ||
			currentInteraction?.type != InteractionType.Call
		) {
			return false;
		}
		const interactionQueue = queues.find((x) => x.id === callSession.queueId);

		return (interactionQueue && interactionQueue.isOperator) || false;
	}, [callSession, queues, currentInteraction]);

	const [notePopoverOpenMap, setNotePopoverOpenMap] = useState<
		Record<string, boolean>
	>({});

	const setNotePopoverOpened = (id: string, open: boolean) => {
		setNotePopoverOpenMap((prev) => ({
			...prev,
			[id]: open,
		}));
	};

	const hoverMediaActions = useCallback(
		(
			contact: ContactListItem,
			showAddToFavorites: boolean,
			isCallMenuOpened: boolean,
			setIsCallMenuOpened: (open: boolean) => void
		) => {
			const isFavoritesLimitReached =
				(favoritesTotalCount.raw || 0) >= appSettings.favoritesLimit;
			const isFavoriteOptionHidden =
				!appSettings.favoritesIsEnabled ||
				(showAddToFavorites && isFavoritesLimitReached);

			return (
				<Flex className={classes.endMedia}>
					<CallMenuItems
						contact={contact}
						onTransferClick={handleTransferClick}
						onCallClick={handleOnCallClick}
						isOutboundPossible={isOutboundPossible}
						isOutboundAllowed={isOutboundAllowed}
						hasAnyOutboundRightEndpoints={hasAnyOutboundRightEndpoints}
						onAgendaClick={() => {}}
						callSession={callSession}
						isCallMenuOpened={isCallMenuOpened}
						setIsCallMenuOpened={setIsCallMenuOpened}
					/>
					<Button
						title={intl.formatMessage({ id: "Interaction.OutboundEmail" })}
						shape="circular"
						appearance="subtle"
						icon={<Mail20Regular />}
						onClick={() => {
							handleOnEmailClick(contact);
						}}
						className={classes.button}
						disabled={
							!(
								email.isOutboundPossible &&
								email.isOutboundAllowed &&
								email.hasAnyOutboundRightEndpoints &&
								(contact.email || contact.userPrincipalName)
							)
						}
					/>
					{areOperatorTemplatesVisible && (
						<OperatorTemplates contact={contact} callSession={callSession} />
					)}
					<Button
						title={intl.formatMessage({ id: "Interaction.Chat" })}
						shape="circular"
						appearance="subtle"
						disabled={(contact.userPrincipalName ?? "").length == 0}
						icon={<CommentMultiple24Regular />}
						className={classes.button}
						onClick={() => {
							addBreadcrumb(
								"ui_interaction",
								"Opening chat with contact",
								contact
							);
							openUserChat(contact.userPrincipalName ?? "");
						}}
					/>
					<ExtendedMenuItems
						notesPopover={buildNotesPopover(contact)}
						showAddToFavorites={showAddToFavorites}
						hideFavoriteOption={isFavoriteOptionHidden}
						isContactEditingAllowed={!isMobileMedium}
						favoriteContactsIds={
							favoritesResult.data?.pages
								.flatMap((x) => x.items)
								.map((x: any) => x.id) ?? []
						}
						contact={contact}
						onEditClick={() => {
							addBreadcrumb("ui_interaction", "Editing contact", contact);
							setContactInEditModeOrToBeDeleted(contact);
							setIsContactDialogOpen(true);
						}}
						onAgendaClick={() => {
							addBreadcrumb(
								"ui_interaction",
								"Opening agenda for contact",
								contact
							);
							setAgendaContactUPN(contact.userPrincipalName);
							setIsAgendaDialogOpen(true);
						}}
						onRemoveClick={(contact: ContactListItem) => {
							addBreadcrumb("ui_interaction", "Removing contact", contact);
							setContactInEditModeOrToBeDeleted(contact);
							setIsConfirmationDialogOpen(true);
						}}
						onFavoriteClick={(contact: ContactListItem) => {
							if (!appSettings.favoritesIsEnabled) {
								return;
							}

							if (!showAddToFavorites) {
								addBreadcrumb(
									"ui_interaction",
									"Remove contact from favorites",
									contact
								);
								removeFromFavoriteMutation.mutate(contact.id);
							} else if (!isFavoritesLimitReached) {
								addBreadcrumb(
									"ui_interaction",
									"Add Contact to favorites",
									contact
								);
								addToFavoritesMutation.mutate(contact.id);
							}
						}}
					/>
				</Flex>
			);
		},
		[
			removeContactMutation,
			addToFavoritesMutation,
			removeFromFavoriteMutation,
			appSettings,
			callSession,
			isOutboundPossible,
			isOutboundAllowed,
			notePopoverOpenMap,
			notesContactId,
			contactNotes,
		]
	);

	const [usersPresences, setUsersPresences] = useState<TeamsPresence[]>([]);

	const badgeStatus = useCallback<any>(
		(userId: string) => {
			if (!!usersPresences.length) {
				const presence = usersPresences.find((x) => x.id == userId);
				if (!presence) return undefined;

				return {
					status: mapPresenceToStatus(presence.availability),
					outOfOffice: false,
				};
			}

			return undefined;
		},
		[usersPresences]
	);

	const buildAvatarPopover = useCallback(
		(contact: ContactListItem) => (
			<PopoverView
				openOnHover
				mouseLeaveDelay={50}
				mountNode={popoverContainerRef}
				trigger={
					<ContactAvatar
						id={contact.id}
						name={contact.name}
						contactType={contact.contactType}
						badge={badgeStatus(contact.id)}
						className={contact.hasCC4TeamsClient ? classes.hasClientBorder : ""}
					/>
				}
				renderOutsideTheDOM={true}
				content={
					<ProfilePicture
						id={contact.id}
						name={contact.name}
						contactType={contact.contactType}
						hasCC4TeamsClient={contact.hasCC4TeamsClient}
						// The contact itself, not a literal: a fresh object each render would defeat the
						// card's detail-row memo. ContactListItem satisfies ProfilePictureContactDetails.
						details={contact}
					/>
				}
			/>
		),
		[usersPresences, badgeStatus]
	);

	useEffect(() => {
		const fetchNotes = async () => {
			if (notesContactId !== undefined) {
				setContactNotes(await getNotesByContactId(notesContactId));
			}
		};

		fetchNotes();
	}, [notesContactId]);

	const afterCreatingNote = (newNote: Note) => {
		setContactNotes([...contactNotes, newNote]);

		switch (selectedValue) {
			case ContactGroupType.Favorites:
				refetchQueries(FavoritesKey);
				return;
			case ContactGroupType.All:
				refetchQueries(ContactsKey);
				return;
			case ContactGroupType.MyQueues:
				refetchQueries(MyQueuesContactsKey);
				return;
			default:
				return;
		}
	};

	const containerRef = useRef<HTMLDivElement>(null);

	const buildNotesPopover = useCallback(
		(contact: ContactListItem) => {
			const isNotePopoverOpen = !!notePopoverOpenMap[contact.id];

			return (
				<PopoverView
					mouseLeaveDelay={50}
					mountNode={notesPopoverContainerRef}
					trigger={
						<Button
							title={intl.formatMessage({
								id: "Contacts.Menu.ContactNotes",
							})}
							shape="circular"
							appearance="subtle"
							icon={<PersonNote20Regular />}
							className={mergeClasses("keep-open", classes.notes)}
							onClick={(ev: React.MouseEvent) => {
								ev.preventDefault();
								ev.stopPropagation();
								setNotesContactId(isNotePopoverOpen ? undefined : contact.id);
								setNotePopoverOpened(contact.id, !isNotePopoverOpen);
							}}
						/>
					}
					open={isNotePopoverOpen}
					renderOutsideTheDOM={true}
					content={
						<div
							className="keep-open"
							onClick={(ev: React.MouseEvent) => {
								ev.preventDefault();
								ev.stopPropagation();
							}}
							ref={containerRef}
							tabIndex={-1}
							onBlur={(e) => {
								const currentTarget = e.currentTarget;
								const relatedTarget = e.relatedTarget as Node | null;

								if (!currentTarget.contains(relatedTarget)) {
									setNotesContactId(isNotePopoverOpen ? undefined : contact.id);
									setNotePopoverOpened(contact.id, !isNotePopoverOpen);
								}
							}}
						>
							<InteractionNotesPopover
								type={NoteType.ContactNote}
								interactionNotes={contactNotes}
								contactId={contact.id}
								agentName={agentState.fullName}
								closePopover={() => {
									setNotesContactId(isNotePopoverOpen ? undefined : contact.id);
									setNotePopoverOpened(contact.id, !isNotePopoverOpen);
								}}
								afterCreatingNote={afterCreatingNote}
								editMode={true}
							/>
						</div>
					}
				/>
			);
		},
		[notePopoverOpenMap, notesContactId, contactNotes]
	);

	const hasSearchResults = useMemo(
		() =>
			(!searchText && isAdvancedFilterEmpty(advancedFilter)) ||
			favoritesResult.isLoading ||
			myQueuesContactsResult.isLoading ||
			contactsResult.isLoading ||
			!!favoritesResult.data?.pages.flatMap((x) => x.items).length ||
			!!contactsResult.data?.pages.flatMap((x) => x.items).length ||
			!!myQueuesContactsResult.data?.pages.flatMap((x) => x.items).length,
		[
			searchText,
			advancedFilter,
			favoritesResult,
			contactsResult,
			myQueuesContactsResult,
		]
	);

	useEffect(() => {
		if (appSettings.contactsPresenceRetrievalInterval == 0) return;

		if (
			(!favoritesResult.isFetched && !contactsResult.isFetched) ||
			favoritesResult.isFetching ||
			contactsResult.isFetching
		)
			return;

		const contactIds =
			contactItems
				.filter(
					(contact: ListItemProps) =>
						contact.extraInfo != ContactType.Database.toString()
				)
				.map((contact: ListItemProps) => contact.key) ?? [];

		const myQueueContectsIds =
			myQueuesContactItems
				.filter(
					(contact: ListItemProps) =>
						contact.extraInfo != ContactType.Database.toString()
				)
				.map((contact: ListItemProps) => contact.key) ?? [];

		const favoriteIds =
			favoriteItems
				.filter(
					(contact: ListItemProps) =>
						contact.extraInfo != ContactType.Database.toString()
				)
				.map((favorite: ListItemProps) => favorite.key) ?? [];

		const allUserIds = contactIds
			.concat(favoriteIds)
			.concat(myQueueContectsIds);

		if (!allUserIds.length) return;

		updateUserPresences(allUserIds);
	}, [
		favoritesResult.isFetching,
		favoritesResult.isFetched,
		myQueuesContactsResult.isFetching,
		myQueuesContactsResult.isFetched,
		contactsResult.isFetching,
		contactsResult.isFetched,
	]);

	const [recurringUpdateUserPresences] = useDebounce((allUserIds: string[]) => {
		updateUserPresences(allUserIds);
	}, appSettings.contactsPresenceRetrievalInterval * 1000);

	const updateUserPresences = async (allUserIds: string[]) => {
		try {
			if (!allUserIds || allUserIds.length === 0) {
				return;
			}

			const presences = await getTeamsPresences(allUserIds);
			setUsersPresences(presences?.value ?? []);

			recurringUpdateUserPresences(allUserIds);
		} catch (error: any) {
			captureError(error, "Failed to fetch Teams presences", undefined, {
				userIdCount: allUserIds.length,
				errorMessage: error?.message,
				errorResponse: error?.response?.data,
				statusCode: error?.response?.status,
			});
		}
	};

	const [callMenuOpenMap, setCallMenuOpenMap] = useState<
		Record<string, boolean>
	>({});

	const setCallMenuOpened = (id: string, open: boolean) => {
		setCallMenuOpenMap((prev) => ({
			...prev,
			[id]: open,
		}));
	};

	const mapContactToList = (
		items: ContactListItem[] | undefined,
		showAddToFavorites: boolean
	) => {
		if (items === undefined) {
			return [];
		}

		return items.map((contact: ContactListItem) => {
			const isCallMenuOpened = !!callMenuOpenMap[contact.id];

			const notePopoverOpenedKeys = Object.keys(notePopoverOpenMap).filter(
				(key) => notePopoverOpenMap[key]
			);

			return {
				key: contact.id,
				media: buildAvatarPopover(contact),
				header: contact.name,
				subHeader: mapContactDescription(contact),
				headerMedia:
					contact.hasContactNotes && contactNotesEnabled ? (
						<PersonNote20Regular className={classes.noteIcon} />
					) : (
						<></>
					),
				extraInfo: contact.contactType.toString(),
				endMedia: hoverMediaActions(
					contact,
					showAddToFavorites,
					isCallMenuOpened,
					(open: boolean) => setCallMenuOpened(contact.id, open)
				),
				isCallMenuOpened: isCallMenuOpened,
				notePopoverOpenedKeys: notePopoverOpenedKeys,
			};
		});
	};

	const favoriteItems = useMemo<ListItemProps[]>(
		() =>
			mapContactToList(
				favoritesResult.data?.pages.flatMap((x) => x.items),
				false
			),
		[
			favoritesResult,
			favoritesInView,
			mapContactDescription,
			hoverMediaActions,
			buildAvatarPopover,
			usersPresences,
			callMenuOpenMap,
			notePopoverOpenMap,
		]
	);

	const contactItems = useMemo<ListItemProps[]>(
		() =>
			mapContactToList(
				contactsResult.data?.pages.flatMap(
					(x: PagedResult<ContactListItem>) => x.items
				),
				true
			),
		[
			contactsResult,
			contactsInView,
			mapContactDescription,
			hoverMediaActions,
			buildAvatarPopover,
			usersPresences,
			callMenuOpenMap,
			notePopoverOpenMap,
		]
	);

	const myQueuesContactItems = useMemo<ListItemProps[]>(
		() =>
			mapContactToList(
				myQueuesContactsResult.data?.pages.flatMap(
					(x: PagedResult<ContactListItem>) => x.items
				),
				true
			),
		[
			myQueuesContactsResult,
			myQueuesContactsInView,
			mapContactDescription,
			hoverMediaActions,
			buildAvatarPopover,
			usersPresences,
			callMenuOpenMap,
			notePopoverOpenMap,
		]
	);

	const buildItem = useCallback(
		(
			items: ListItemProps[],
			inView: InViewHookResponse,
			hasNextPage?: boolean
		) => (
			<List
				classesName={{
					mainText: classes.listItemHeader,
					shadow: shadow,
					startMedia: classes.startMedia,
				}}
				items={items}
				nextPageSpinner={
					!!hasNextPage ? (
						<Spinner
							ref={inView.ref}
							className={classes.spinner}
							size="tiny"
							label={intl.formatMessage({ id: "Common.Loading" })}
						/>
					) : undefined
				}
			/>
		),
		[intl, classes]
	);

	useEffect(() => {
		setSelectedValue(
			appStoreCache[CacheKey.ContactsSelectedTab] ?? ContactGroupType.All
		);
	}, []);

	const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
		setSelectedValue(data.value);
		appStoreActions.setCache(CacheKey.ContactsSelectedTab, data.value);
	};

	const buildTab = (
		type: ContactGroupType,
		label: string,
		count: number,
		icon: any
	) => {
		return (
			<Tab
				value={type}
				title={`${label} (${count})`}
				icon={icon}
				className={classes.tab}
			>
				{count}
			</Tab>
		);
	};

	return (
		<>
			<Flex column className={mergeClasses(classes.container, shadow)}>
				<PanelHeader
					left={
						<Text className={classes.headerTitle}>
							{intl.formatMessage({ id: "Contacts.HeaderTitle" })}
						</Text>
					}
					right={
						<>
							<TabList
								selectedValue={selectedValue}
								appearance="filled-circular"
								size="small"
								className={classes.tabList}
								onTabSelect={onTabSelect}
							>
								{buildTab(
									ContactGroupType.All,
									intl.formatMessage({ id: "Contacts.All" }),
									contactsTotalCount.filtered,
									<PeopleFilled fontSize="16" />
								)}
								{buildTab(
									ContactGroupType.MyQueues,
									intl.formatMessage({ id: "Contacts.MyQueues" }),
									myQueuesContactsTotalCount.filtered,
									<PeopleQueueFilled fontSize="16" />
								)}
								{buildTab(
									ContactGroupType.Favorites,
									intl.formatMessage({ id: "Contacts.Favorites" }),
									favoritesTotalCount.filtered,
									<StarFilled fontSize="16" />
								)}
							</TabList>
							{!isMobileMedium && (
								<Button
									appearance="primary"
									shape="circular"
									icon={<PeopleAdd24Regular />}
									onClick={() => {
										setContactInEditModeOrToBeDeleted({} as ContactListItem);
										setIsContactDialogOpen(true);
									}}
								/>
							)}
						</>
					}
				/>

				<Flex gap={GapSize.Small} className={classes.searchFilterBar}>
					<ClearableInput
						clearable
						value={searchText}
						className={classes.searchInput}
						placeholder={intl.formatMessage({
							id: "Contacts.SearchPlaceholder",
						})}
						contentAfter={<Search24Regular />}
						onChange={setSearchText}
					/>
					<Flex>
						{canUseAdvancedFilter && (
							<AdvancedFilterButton
								count={countAdvancedFilterValues(advancedFilter)}
								onClick={() => setAdvancedFilterDialogOpen(true)}
							/>
						)}
						<ContactTypeFilter
							value={contactTypeFilter}
							onChange={onContactTypeFilterChange}
							disabled={selectedValue === ContactGroupType.MyQueues}
						/>
						<QueueFilter
							cacheKey={ContactsQueueIdsCacheKey}
							onSelectedQueuesChange={setFilteredQueueIds}
						/>
					</Flex>
				</Flex>
				{canUseAdvancedFilter && (
					<ActiveFilterChips
						value={advancedFilter}
						onRemove={(key: AdvancedFilterFieldKey, value: string) =>
							onAdvancedFilterChange(
								removeAdvancedFilterValue(advancedFilter, key, value)
							)
						}
						onClearAll={() => onAdvancedFilterChange(emptyAdvancedFilter())}
					/>
				)}
				{!hasSearchResults && (
					<Text className={classes.noResultText}>
						{intl.formatMessage({ id: "Contacts.SearchNoResults" })}
					</Text>
				)}
				{hasSearchResults && (
					<>
						{appSettings.favoritesIsEnabled &&
							selectedValue === ContactGroupType.Favorites &&
							buildItem(
								favoriteItems,
								favoritesInView,
								favoritesResult.hasNextPage
							)}
						{selectedValue === ContactGroupType.MyQueues &&
							buildItem(
								myQueuesContactItems,
								myQueuesContactsInView,
								myQueuesContactsResult.hasNextPage
							)}
						{selectedValue === ContactGroupType.All &&
							buildItem(
								contactItems,
								contactsInView,
								contactsResult.hasNextPage
							)}
					</>
				)}
			</Flex>
			<AdvancedFilterDialog
				open={isAdvancedFilterDialogOpen}
				setOpen={setAdvancedFilterDialogOpen}
				value={advancedFilter}
				onApply={onAdvancedFilterChange}
			/>
			<DialogView
				content={
					<ContactForm
						contact={contactInEditModeOrToBeDeleted ?? ({} as ContactListItem)}
						saveContact={saveContact}
						closeDialog={closeContactDialog}
						queueSelectionEnabled
						isSaveInProgress={isSaveInProgress}
					/>
				}
				title={
					contactInEditModeOrToBeDeleted.id
						? intl.formatMessage({ id: "Contacts.Menu.EditContact" })
						: intl.formatMessage({ id: "Contacts.AddContact" })
				}
				open={isContactDialogOpen}
				setOpen={setIsContactDialogOpen}
			/>
			<DialogView
				content={
					<div className={agendaClasses.userAgendaPopupContent}>
						<AgendaContentPopover userPrincipalName={agendaContactUPN} />
					</div>
				}
				open={isAgendaDialogOpen}
				setOpen={setIsAgendaDialogOpen}
				size={DialogSize.Large}
			/>
			<ConfirmationDialog
				open={isConfirmationDialogOpen}
				setOpen={setIsConfirmationDialogOpen}
				text={`${intl.formatMessage({ id: "Contacts.RemoveConfirmation" })} ${
					contactInEditModeOrToBeDeleted.name
				}`}
				onConfirm={() => {
					addBreadcrumb(
						"ui_interaction",
						`Confirmed removal of contact with id ${contactInEditModeOrToBeDeleted.id}`
					);
					removeContactMutation.mutate(contactInEditModeOrToBeDeleted.id);
				}}
				onCancel={() => {
					setIsConfirmationDialogOpen(false);
				}}
			/>
			<div id="popover-container" ref={popoverContainerRef}></div>
			<div id="notes-popover-container" ref={notesPopoverContainerRef}></div>
		</>
	);
};
